import {useCallback, useMemo} from 'react'
import {useCustomerId, useCustomerProductLists, useCustomerProductList} from '@salesforce/commerce-sdk-react'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import type {WishlistProductIdsMap, WishlistType} from '_types'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCustomApi} from '_customHooks'
import {getAbsoluteUrl} from '_utils'
import {useGlobalConfigState} from '_context'

const onClient = typeof window !== 'undefined'

/**
 * @name useWishlist
 *
 * @hook
 *
 * @description
 * Custom hook to handle customer wishlist.
 *
 * @remarks
 * A customer must have 2 different wishlists, a private one and a public one.
 * Public wishlist id must be used for share business logic.
 * Since IDs of product resources in the wishlist are different from the SKU of the product itself
 * there is a map that keeps track of the association between private and public resource IDs.
 *
 * @returns An object containing wishlist informations.
 */
const useWishlist = () => {
    const [searchParams] = useSearchParams()
    const searchParamId = searchParams.id as string
    const customerId = useCustomerId()
    const {locale, site} = useMultiSite()

    const {usePublicProductList} = useCustomApi()
    const {appPreferences} = useGlobalConfigState()
    const {hostLink} = appPreferences
    const hostLinkLocalized = hostLink ? JSON.parse(hostLink)[site.id] : undefined

    const wishlistType: WishlistType = searchParamId ? 'public' : 'private'

    const {data: productLists, refetch: refetchWishlists} = useCustomerProductLists(
        {
            parameters: {customerId},
        },
        {
            enabled: onClient && wishlistType === 'private' && !!customerId,
        },
    )

    const wishlists = useMemo(() => productLists?.data?.filter((list) => list.type === 'wish_list'), [productLists])
    const privateWishlist = useMemo(() => wishlists?.find((wishlist) => wishlist.public === false), [wishlists])
    const publicWishlist = useMemo(() => wishlists?.find((wishlist) => wishlist.public === true), [wishlists])

    const publicWishlistId = publicWishlist?.id || searchParamId
    const privateWishlistId = privateWishlist?.id

    const wishlistId = wishlistType === 'public' ? publicWishlistId : privateWishlistId

    const publicWishlistUrl = getAbsoluteUrl(hostLinkLocalized, locale.alias, `/wishlist?id=${publicWishlistId}`)

    const {data: wishlistPrivateData, ...restOfPrivateWishlistQuery} = useCustomerProductList(
        {
            parameters: {customerId, listId: wishlistId!, c_locale: locale.id},
        },
        {
            enabled: onClient && wishlistType === 'private' && !!customerId && !!wishlistId,
        },
    )

    const {data: publicWishlistData, ...restOfPublicWishlistQuery} = usePublicProductList(locale.id, wishlistId!, {
        enabled: onClient && wishlistType === 'public' && !!wishlistId,
    })

    const wishlist = wishlistType === 'private' ? wishlistPrivateData : publicWishlistData
    const restOfWishlist = wishlistType === 'private' ? restOfPrivateWishlistQuery : restOfPublicWishlistQuery
    const wishlistProductsCount = useMemo(() => wishlist?.customerProductListItems?.length ?? 0, [wishlist])
    const isWishlistEmpty = wishlistProductsCount === 0

    /**
     * @name buildWishlistMapId
     *
     * @description
     * This function builds a map containing a `privateId` and `publicId` for each product in the wishlist.
     * The IDs refer to the available wishlists.
     *
     * @returns {WishlistProductIdsMap} Result map.
     */
    const buildWishlistMapId = useCallback((): WishlistProductIdsMap => {
        const wishlistMapId = new Map<string, {privateId: string; publicId: string}>()

        if (privateWishlist && publicWishlist) {
            privateWishlist.customerProductListItems?.forEach((privateItem) => {
                const publicItem = publicWishlist.customerProductListItems?.find((item) => item.productId === privateItem.productId)

                if (privateItem.productId && privateItem.id && publicItem?.id) {
                    wishlistMapId.set(privateItem.productId, {
                        privateId: privateItem.id,
                        publicId: publicItem.id,
                    })
                }
            })
        }

        return wishlistMapId
    }, [privateWishlist, publicWishlist])

    /**
     * @name wishlistMapId
     *
     * @description
     * Contains a `privateId` and `publicId` for each product in the wishlist.
     */
    const wishlistMapId: Map<string, {privateId: string; publicId: string}> | undefined = buildWishlistMapId()

    /**
     * @name matchWishlistItem
     *
     * @description
     * Finds a wishlist line item whose stored `productId` corresponds to the given `id`, using the
     * SAME tolerant matching as {@link isProductInWishlist}: for single-size products (size code `01`)
     * the stored `productId` carries a size suffix (e.g. `...C60001`) that won't equal the shorter
     * variation/master id, so we match by substring; otherwise we match exactly.
     *
     * @remarks
     * The removal flow (`removeItemFromWishlist`) resolves the line-item id to delete via the getters
     * below. Previously they used an exact `wishlistMapId.get(id)`, which returned `undefined` when the
     * item was stored under a size-suffixed SKU — so `isProductInWishlist` reported the product as
     * present but nothing could be deleted. Reusing this matcher keeps "is it in the wishlist" and
     * "which line item to delete" consistent.
     */
    const matchWishlistItem = (
        items: Array<{id?: string; productId?: string; c_productWishlist?: {c_variantData?: {size?: {code?: string}}}}> | undefined,
        id: string,
    ) => {
        // Exact match first (a fully-qualified, size-specific SKU).
        const exact = items?.find((item) => item.productId === id)
        if (exact) return exact

        // Single-size / variation-group fallback: the item was stored under a size-suffixed SKU
        // (e.g. `...C60001`) while the lookup id is the shorter variation/master id (e.g. `...C600`).
        // Prefer the size-code hint when present (detailed wishlist query), otherwise fall back to a
        // prefix match on the stored productId (list-level query has no c_variantData).
        return items?.find(
            (item) =>
                (item?.c_productWishlist?.c_variantData?.size?.code === '01' && item.productId?.includes(id)) ||
                (!!id && !!item.productId && item.productId.startsWith(id)),
        )
    }

    /**
     * Getter function for private wishlist id of a product.
     * @param {string} productId
     * @returns Private wishlist id.
     */
    const getProductPrivateWishlistId = (productId: string): string | undefined => {
        return matchWishlistItem(privateWishlist?.customerProductListItems, productId)?.id ?? wishlistMapId?.get(productId)?.privateId
    }

    /**
     * Getter function for public wishlist id of a product.
     * @param {string} productId
     * @returns Public wishlist id.
     */
    const getProductPublicWishlistId = (productId: string): string | undefined => {
        return matchWishlistItem(publicWishlist?.customerProductListItems, productId)?.id ?? wishlistMapId?.get(productId)?.publicId
    }

    /**
     * @name isProductInWishlist
     *
     * @description
     * Checks if the product is in the wishlist by verifying
     * if the product ID is a substring of any wishlist item ID.
     *
     * @remarks
     * This handles cases where the wishlist item ID includes additional
     * information like size (e.g., product ID "251MH1267340C600" would match wishlist item
     * "251MH1267340C60005" which includes the size code).
     *
     * @param {string} productId
     * @param {string | null} variantId
     * @param {string} [variant]
     * @returns {boolean} True if it's in the wishlist, false otherwise.
     */
    const isProductInWishlist = useCallback(
        (productId: string, variantId?: string | null, variant?: string): boolean => {
            if (variant === 'wishlist') {
                return !!wishlist?.customerProductListItems?.find((item) => item.productId === productId)
            } else {
                const id = variantId || productId
                return !!wishlist?.customerProductListItems?.find((item) => {
                    if (item?.c_productWishlist?.c_variantData?.size?.code === '01' && item.productId?.includes(id)) {
                        //Handling single size
                        return true
                    } else {
                        return item.productId === id
                    }
                })
            }
        },
        [wishlist],
    )

    return {
        wishlist,
        ...restOfWishlist,
        wishlistType,
        wishlistProductsCount,
        wishlistMapId,
        isWishlistEmpty,
        publicWishlistId,
        privateWishlistId,
        publicWishlistUrl,
        refetchWishlists,
        isProductInWishlist,
        getProductPrivateWishlistId,
        getProductPublicWishlistId,
    }
}

export * from './actions'
export {useWishlist}
